<template>
  <span class="controlled-gif" :class="{ playing }">
    <img
      v-if="stillFrameUrl"
      class="controlled-gif-layer controlled-gif-still"
      :src="stillFrameUrl"
      :alt="alt"
      draggable="false"
      @error="handleStillFrameError"
    />
    <img
      :key="motionKey"
      class="controlled-gif-layer controlled-gif-motion"
      :class="{ 'fallback-visible': !stillFrameUrl }"
      :src="src"
      :alt="alt"
      draggable="false"
    />
  </span>
</template>

<script lang="ts">
import { defineComponent, onUnmounted, ref, watch } from 'vue';

const canvasToObjectUrl = (canvas: HTMLCanvasElement): Promise<string> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create still frame blob'));
        return;
      }

      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
};

const drawImageToObjectUrl = async (
  image: CanvasImageSource,
  width: number,
  height: number
): Promise<string> => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is not available');
  }

  context.drawImage(image, 0, 0, width, height);
  return canvasToObjectUrl(canvas);
};

const extractWithImageDecoder = async (src: string): Promise<string> => {
  const ImageDecoderCtor = (window as any).ImageDecoder;
  if (!ImageDecoderCtor) {
    throw new Error('ImageDecoder is not available');
  }

  const response = await fetch(src, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`Failed to load image: ${response.status}`);
  }

  const blob = await response.blob();
  const buffer = await blob.arrayBuffer();
  const decoder = new ImageDecoderCtor({
    data: buffer,
    type: blob.type || 'image/gif',
  });

  const result = await decoder.decode({ frameIndex: 0 });
  const frame = result.image;
  const width = frame.displayWidth || frame.codedWidth;
  const height = frame.displayHeight || frame.codedHeight;

  try {
    return await drawImageToObjectUrl(frame, width, height);
  } finally {
    frame.close?.();
    decoder.close?.();
  }
};

const extractWithImageElement = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'sync';

    image.onload = async () => {
      try {
        resolve(
          await drawImageToObjectUrl(
            image,
            image.naturalWidth,
            image.naturalHeight
          )
        );
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => {
      reject(new Error('Failed to load image element'));
    };

    image.src = src;
  });
};

export default defineComponent({
  name: 'ControlledGif',
  props: {
    src: {
      type: String,
      required: true,
    },
    playing: {
      type: Boolean,
      default: false,
    },
    alt: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const stillFrameUrl = ref('');
    const motionKey = ref(0);
    let currentObjectUrl = '';
    let loadVersion = 0;
    let replayTimer: number | null = null;

    const stopReplayLoop = (): void => {
      if (replayTimer !== null) {
        window.clearInterval(replayTimer);
        replayTimer = null;
      }
    };

    const startReplayLoop = (): void => {
      stopReplayLoop();
      motionKey.value++;
      if (
        process.env.NODE_ENV === 'development' ||
        localStorage.getItem('avatarGifDebug') === '1'
      ) {
        console.log('[ControlledGif] start replay', {
          src: props.src,
          motionKey: motionKey.value,
        });
      }
      replayTimer = window.setInterval(() => {
        motionKey.value++;
        if (localStorage.getItem('avatarGifDebug') === '1') {
          console.log('[ControlledGif] replay tick', {
            src: props.src,
            motionKey: motionKey.value,
          });
        }
      }, 1800);
    };

    const revokeStillFrame = (): void => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = '';
      }
      stillFrameUrl.value = '';
    };

    const setStillFrame = (url: string): void => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }

      currentObjectUrl = url;
      stillFrameUrl.value = url;
    };

    const generateStillFrame = async (src: string): Promise<void> => {
      const version = ++loadVersion;
      revokeStillFrame();

      if (!src) {
        return;
      }

      try {
        let url: string;
        try {
          url = await extractWithImageElement(src);
        } catch {
          url = await extractWithImageDecoder(src);
        }

        if (version !== loadVersion) {
          URL.revokeObjectURL(url);
          return;
        }

        setStillFrame(url);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to extract first GIF frame:', error);
        }
      }
    };

    const handleStillFrameError = (): void => {
      revokeStillFrame();
    };

    watch(
      () => props.src,
      (src) => {
        motionKey.value++;
        void generateStillFrame(src);
      },
      { immediate: true }
    );

    watch(
      () => props.playing,
      (playing, wasPlaying) => {
        if (playing && !wasPlaying) {
          startReplayLoop();
          return;
        }

        if (!playing) {
          stopReplayLoop();
          if (
            process.env.NODE_ENV === 'development' ||
            localStorage.getItem('avatarGifDebug') === '1'
          ) {
            console.log('[ControlledGif] stop replay', {
              src: props.src,
              motionKey: motionKey.value,
            });
          }
        }
      }
    );

    onUnmounted(() => {
      stopReplayLoop();
      loadVersion++;
      revokeStillFrame();
    });

    return {
      handleStillFrameError,
      motionKey,
      stillFrameUrl,
    };
  },
});
</script>

<style scoped>
.controlled-gif {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
  position: relative;
}

.controlled-gif-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  border-radius: inherit;
  transition: opacity 180ms ease;
  user-select: none;
}

.controlled-gif-motion {
  opacity: 0;
}

.controlled-gif-motion.fallback-visible {
  opacity: 1;
}

.controlled-gif-still {
  opacity: 1;
}

.controlled-gif.playing .controlled-gif-motion {
  opacity: 1;
}

.controlled-gif.playing .controlled-gif-still {
  opacity: 0;
}
</style>
